import React, {useState} from 'react';
import {List, useRedirect, useListContext, useTranslate, usePermissions, useRefresh} from 'react-admin';
import {Avatar, Box, Pagination, Typography} from '@mui/material';
import {AddRounded} from '@mui/icons-material';
import {t} from '../themes/statamicTokens';
import PageHeader from '../components/statamic/PageHeader';
import PrimaryButton from '../components/statamic/PrimaryButton';
import SavedViewsTabs from '../components/statamic/SavedViewsTabs';
import FilterBar, {FilterDropdown} from '../components/statamic/FilterBar';
import ScopeBanner from '../components/statamic/ScopeBanner';
import useScopedFilter from '../scope/useScopedFilter';
import StatamicTable from '../components/statamic/StatamicTable';
import StatusDot from '../components/statamic/StatusDot';
import DeleteConfirmDialog from '../components/statamic/DeleteConfirmDialog';
import hasPermission from '../auth/hasPermission';

const formatDate = (v) => {
    if (!v) return null;
    const d = new Date(v); return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleDateString();
};

const formatDateTime = (v) => {
    if (!v) return null;
    const d = new Date(v); return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString();
};

const AdminCell = ({record, translate}) => {
    const avatarUrl = record.avatar ? `${process.env.REACT_APP_CDN_URL}/${record.avatar}` : undefined;
    const fullName = [
        record.salutation ? translate(record.salutation) : null,
        record.given_name,
        record.family_name,
    ].filter(Boolean).join(' ') || 'Unnamed';

    return (
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
            <Avatar
                src={avatarUrl}
                sx={{
                    width: 36,
                    height: 36,
                    bgcolor: t.color.sidebarHover,
                    color: t.color.text.secondary,
                    border: `1px solid ${t.color.border.hairline}`,
                    fontSize: 13,
                }}
            >
                {(record.given_name?.charAt(0) ?? '') + (record.family_name?.charAt(0) ?? '')}
            </Avatar>
            <Box sx={{minWidth: 0, display: 'flex', alignItems: 'center', gap: 1}}>
                <StatusDot variant={record.enabled ? 'success' : 'neutral'}/>
                <Typography
                    sx={{
                        fontSize: `${t.type.bodyStrong.size}px`,
                        fontWeight: t.type.bodyStrong.weight,
                        color: t.color.text.primary,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {fullName}
                </Typography>
            </Box>
        </Box>
    );
};

const UmbrellaAdminListStatamicInner = () => {
    const redirect = useRedirect();
    const refresh = useRefresh();
    const translate = useTranslate();
    const {permissions} = usePermissions();
    const {filterValues, setFilters, displayedFilters} = useListContext();
    const [activeViewId, setActiveViewId] = useState('enabled');
    const [deleteRecord, setDeleteRecord] = useState(null);
    useScopedFilter('v2UmbrellaOnly');

    const canCreate = hasPermission(permissions, ['create'], 'umbrellaAdmins');
    const canEdit = hasPermission(permissions, ['edit'], 'umbrellaAdmins');
    const canDelete = hasPermission(permissions, ['delete'], 'umbrellaAdmins');

    const views = [
        {id: 'enabled',  label: translate('Enabled')},
        {id: 'disabled', label: translate('Disabled')},
        {id: 'all',      label: translate('All')},
    ];

    const handleViewSelect = (id) => {
        setActiveViewId(id);
        const base = {...filterValues};
        delete base.enabled;
        if (id === 'enabled')       setFilters({...base, enabled: true}, displayedFilters);
        else if (id === 'disabled') setFilters({...base, enabled: false}, displayedFilters);
        else                        setFilters(base, displayedFilters);
    };

    const goToAdmin = (id) => redirect('show', 'umbrellaAdmins', id);

    const rowActions = (record) => [
        {label: translate('View'), onClick: () => redirect('show', 'umbrellaAdmins', record.id)},
        ...(canEdit ? [{label: translate('Edit'), onClick: () => redirect('edit', 'umbrellaAdmins', record.id)}] : []),
        ...(canDelete ? [{label: translate('Delete'), variant: 'danger', onClick: () => setDeleteRecord(record)}] : []),
    ];

    const columns = [
        {
            key: 'admin', label: translate('Umbrella admin'), sortable: true, sortField: 'family_name',
            render: (r) => <AdminCell record={r} translate={translate}/>,
        },
        {
            key: 'email', label: translate('Email'), width: 240,
            render: (r) => (
                <Typography sx={{fontSize: `${t.type.body.size}px`, color: t.color.text.secondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                    {r.email || '—'}
                </Typography>
            ),
        },
        {
            key: 'umbrella', label: translate('Umbrella'), width: 220,
            render: (r) => (
                <Typography sx={{fontSize: `${t.type.body.size}px`, color: t.color.text.secondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                    {r.umbrella?.name || '—'}
                </Typography>
            ),
        },
        {
            key: 'registered', label: translate('Registration date'), sortable: true, sortField: 'create_date_time', width: 140,
            render: (r) => (
                <Typography sx={{fontSize: `${t.type.body.size}px`, color: t.color.text.secondary}}>
                    {formatDate(r.create_date_time) ?? '—'}
                </Typography>
            ),
        },
        {
            key: 'lastseen', label: translate('Last access date'), width: 160,
            render: (r) => (
                <Typography sx={{fontSize: `${t.type.body.size}px`, color: t.color.text.secondary}}>
                    {formatDateTime(r.last_access_date_time) ?? '—'}
                </Typography>
            ),
        },
    ];

    const updateFilter = (src, v) => setFilters({...filterValues, [src]: v}, displayedFilters);
    const clearFilter = (src) => {
        const next = {...filterValues};
        delete next[src];
        setFilters(next, displayedFilters);
    };

    return (
        <Box sx={{backgroundColor: t.color.page, minHeight: '100%'}}>
            <PageHeader
                title={translate('Umbrella Admins')}
                subtitle={translate('Umbrella-level administrators')}
                actions={
                    canCreate ? (
                        <PrimaryButton
                            variant="primary"
                            startIcon={<AddRounded sx={{fontSize: 18}}/>}
                            onClick={() => redirect('create', 'umbrellaAdmins')}
                        >
                            {translate('Create umbrella admin')}
                        </PrimaryButton>
                    ) : null
                }
            />

            <Box
                sx={{
                    backgroundColor: t.color.surface,
                    border: `1px solid ${t.color.border.default}`,
                    borderRadius: `${t.radius.md}px`,
                    mx: 3,
                    mb: 3,
                    overflow: 'hidden',
                }}
            >
                <ScopeBanner/>
                <SavedViewsTabs
                    views={views}
                    activeId={activeViewId}
                    onSelect={handleViewSelect}
                    onAdd={() => {}}
                />

                <FilterBar
                    searchValue={filterValues?.q}
                    onSearchChange={(q) => setFilters({...filterValues, q}, displayedFilters)}
                    placeholder={translate('Search umbrella admins')}
                    filters={
                        <FilterDropdown
                            label={translate('Enabled')}
                            source="enabled"
                            type="boolean"
                            value={filterValues?.enabled}
                            onChange={updateFilter}
                            onClear={clearFilter}
                        />
                    }
                />

                <StatamicTable
                    columns={columns}
                    rowActions={rowActions}
                    onRowClick={(record) => goToAdmin(record.id)}
                />

                <StatamicPagination/>
            </Box>

            {deleteRecord && (
                <DeleteConfirmDialog
                    open
                    onClose={() => setDeleteRecord(null)}
                    resource="umbrellaAdmins"
                    record={deleteRecord}
                    recordLabel={[deleteRecord.given_name, deleteRecord.family_name].filter(Boolean).join(' ') || deleteRecord.email || undefined}
                    onSuccess={refresh}
                />
            )}
        </Box>
    );
};

const StatamicPagination = () => {
    const {page, perPage, total, setPage} = useListContext();
    const pageCount = Math.max(1, Math.ceil((total ?? 0) / (perPage ?? 25)));
    if (pageCount <= 1) return null;

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 3,
                py: 1.5,
                borderTop: `1px solid ${t.color.border.hairline}`,
            }}
        >
            <Typography sx={{fontSize: `${t.type.caption.size}px`, color: t.color.text.muted}}>
                Showing page {page} of {pageCount} · {total ?? 0} total
            </Typography>
            <Pagination
                count={pageCount}
                page={page}
                onChange={(_, p) => setPage(p)}
                size="small"
                shape="rounded"
                sx={{
                    '& .MuiPaginationItem-root': {
                        fontSize: `${t.type.caption.size}px`,
                        color: t.color.text.secondary,
                        borderColor: t.color.border.default,
                    },
                    '& .Mui-selected': {
                        backgroundColor: `${t.color.accent.primaryTint} !important`,
                        color: `${t.color.accent.primary} !important`,
                    },
                }}
            />
        </Box>
    );
};

const UmbrellaAdminListStatamic = (props) => (
    <List
        {...props}
        sort={{field: 'create_date_time', order: 'DESC'}}
        perPage={25}
        filterDefaultValues={{enabled: true}}
        actions={false}
        pagination={false}
        component={Box}
        sx={{
            backgroundColor: t.color.page,
            '& .RaList-main': {backgroundColor: t.color.page},
        }}
    >
        <UmbrellaAdminListStatamicInner/>
    </List>
);

export default UmbrellaAdminListStatamic;
